import { Component, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { map } from 'rxjs/operators';
import {
  BookingService,
  BranchService,
  CustomerService,
  EmployeeService,
  ServiceService,
  ServiceCategoryService,
  RoomService,
  BookingSourceService,
  trackByIndex,
  User,
  VisitStatusPipe
} from '@beautyapp/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import subMonths from 'date-fns/subMonths';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import { AddAppointmentComponent } from '../../booking-modal/components/add-appointment/add-appointment.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { format } from 'date-fns';
import { ClearAddAppointmentSpecService } from '../../appointment.actions';
import { VisitManageComponent } from '../../visit-modal/visit-manage.component';
import { rxResource } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { SelectSearchComponent, SelectComponent } from '@beautyapp/form';
import { NgxPermissionsModule } from 'ngx-permissions';
import { FormsModule } from '@angular/forms';
import { NzSpaceComponent } from 'ng-zorro-antd/space';

@UntilDestroy()
@Component({
    selector: 'beautyapp-appointments',
    templateUrl: './appointments.component.html',
    styleUrls: ['./appointments.component.scss'],
    standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    NzGridModule,
    NzTableModule,
    NzButtonModule,
    NzIconModule,
    NzTooltipModule,
    NzDatePickerModule,
    NzTagModule,
    NzBadgeModule,
    NzCheckboxModule,
    SelectSearchComponent,
    SelectComponent,
    NgxPermissionsModule,
    NzSpaceComponent,
    VisitStatusPipe
  ]
})
export class AppointmentsComponent {
  private service = inject(BookingService);
  private branchService = inject(BranchService);
  private customerService = inject(CustomerService);
  private employeeService = inject(EmployeeService);
  private serviceService = inject(ServiceService);
  private serviceCategoryService = inject(ServiceCategoryService);
  private roomService = inject(RoomService);
  private bookingSourceService = inject(BookingSourceService);
  private modalService = inject(NzModalService);
  private drawerService = inject(NzDrawerService);
  private translateService = inject(TranslateService);
  private fb = inject(UntypedFormBuilder);
  private store = inject(Store);

  // Signals for pagination and filters
  paginatorPageSize = signal(10);
  paginatorPageIndex = signal(1);
  searchForm: UntypedFormGroup;
  searchValue = signal<string | undefined>(undefined);
  branchId = signal<string | undefined>(undefined);
  customers = signal<User[]>([]);

  protected readonly trackByIndex = trackByIndex;

  ranges = {
    დღეს: [new Date(), new Date()],
    'მიმდინარე თვე': [startOfMonth(new Date()), endOfMonth(new Date())],
    'წინა თვე': [
      startOfMonth(subMonths(new Date(), 1)),
      endOfMonth(subMonths(new Date(), 1)),
    ],
    'მიმდინარე წელი': [startOfYear(new Date()), endOfYear(new Date())],
  };

  date: any;
  dateFormat = 'dd/MM/yyyy';
  // Legacy booking-status filter (shown when the branch is NOT on the visit-booking flow)
  statuses = [
    { label: 'ყველა', value: null },
    { label: 'ჩაწერილი', value: 1 },
    { label: 'დასრულებული', value: 2 },
  ];

  // beaut-336: visit-status filter, shown instead of the legacy one when the
  // branch has the visit-booking flow enabled. Labels mirror VisitStatusPipe.
  visitStatuses = [
    { label: 'ყველა', value: null },
    { label: 'მოლოდინში', value: 'PENDING' },
    { label: 'დადასტურებული', value: 'CONFIRMED' },
    { label: 'პროცესში', value: 'IN_PROGRESS' },
    { label: 'არ გამოცხადდა', value: 'NO_SHOW' },
    { label: 'დასარეკია', value: 'NEED_CALL' },
  ];

  // Trigger for bookings reload
  private reloadTrigger = signal(0);

  // rxResource for reactive data fetching with params
  bookingsResource = rxResource({
    params: () => ({
      page: this.paginatorPageIndex(),
      pageSize: this.paginatorPageSize(),
      filters: this.searchForm.value,
      trigger: this.reloadTrigger()
    }),
    stream: ({ params }) => {
      const requestParams = {
        page: params.page,
        pageSize: params.pageSize,
        ...params.filters
      };
      return this.service.paginated(requestParams);
    }
  });

  // Simple rxResources without params - use signal approach instead
  branchesResource = rxResource({
    params: signal({}),
    stream: () => this.branchService.getMyBranches()
  });

  employeesResource = rxResource({
    params: signal({}),
    stream: () => this.employeeService.getDropdown().pipe(
      map((employees: any[]) => employees.map((m: any) => ({...m, fullName: m.user?.fullName})))
    )
  });

  servicesResource = rxResource({
    params: signal({}),
    stream: () => this.serviceService.dropdown()
  });

  activeBranch = this.store.selectSignal(AuthState.getActiveBranch);
  activeBranchId = computed(() => this.activeBranch()?.id || '');
  // beaut-336: toggles which status filter is shown (visit vs legacy booking).
  visitBookingEnabled = computed(() => this.activeBranch()?.visitBookingEnabled ?? false);

  // beaut-336: nz-tag color per visit status for the list "status" column.
  private readonly visitStatusColors: Record<string, string> = {
    PENDING: 'orange',
    CONFIRMED: 'blue',
    IN_PROGRESS: 'processing',
    COMPLETED: 'success',
    NO_SHOW: 'default',
    NEED_CALL: 'magenta',
    CANCELED: 'error',
    DELETED: 'default',
  };
  visitStatusColor(status: string | undefined | null): string {
    return (status && this.visitStatusColors[status]) || 'default';
  }

  roomsResource = rxResource({
    stream: () => this.roomService.getAll()
  })

  bookingSourcesResource = rxResource({
    params: signal({}),
    stream: () => this.bookingSourceService.getAll()
  });

  categoriesResource = rxResource({
    params: signal({}),
    stream: () => this.serviceCategoryService.dropdown()
  });

  // Computed values
  loadData = computed(() => (this.bookingsResource.value() as any)?.data || []);
  total = computed(() => (this.bookingsResource.value() as any)?.meta?.itemCount || 0);
  loading = computed(() => this.bookingsResource.isLoading());
  branches = computed(() => this.branchesResource.value() || []);
  employees = computed(() => this.employeesResource.value() || []);
  services = computed(() => this.servicesResource.value() || []);
  rooms = computed(() => this.roomsResource.value() || []);
  bookingSources = computed(() => this.bookingSourcesResource.value() || []);
  categories = computed(() => this.categoriesResource.value() || []);

  constructor() {
    this.searchForm = this.fb.group({
      startDate: new Date(),
      endDate: new Date(),
      isDeleted: false,
      userId: null,
      employeeId: null,
      createdById: null,
      categoryId: null,
      serviceId: null,
      roomId: null,
      sourceId: null,
      status: null,
      visitStatus: null,
      sortBy: 'bookingDate',
      sortOrder: 'DESC',
    });

    // Listen to form changes and reload bookings
    this.searchForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.paginatorPageIndex.set(1);
      this.reloadBookings();
    });
  }

  isEmoji(icon: string): boolean {
    if (!icon) return false;
    // Check if the icon contains emoji characters or is a single character (emoji)
    // NZ-ZORRO icons are multi-character strings like 'phone', 'mobile', etc.
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u;
    return emojiRegex.test(icon) || (icon.length <= 2 && !/^[a-z]+$/.test(icon));
  }

  private reloadBookings() {
    this.reloadTrigger.update(v => v + 1);
  }

  searchCustomer(search: string) {
    this.customerService
      .search(search)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.customers.set(res);
      });
  }

  paginatorChange(pageEvent: any) {
    this.paginatorPageSize.set(pageEvent.pageSize);
    this.paginatorPageIndex.set(pageEvent.pageIndex);
    const activeSort = pageEvent.sort?.find((s: any) => s.value);
    if (activeSort) {
      this.searchForm.patchValue({
        sortBy: activeSort.key,
        sortOrder: activeSort.value === 'ascend' ? 'ASC' : 'DESC',
      }, { emitEvent: false });
      this.reloadBookings();
    }
  }

  delete(item: any) {
    if (item.id) {
      this.service
        .delete(item.id)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.reloadBookings();
        });
    }
  }

  view(event: any) {
    const title = `${event.operationType}  # ${event.id}`;

    // const drawerRef = this.drawerService.create<OperationDetailComponent, { data: string }, string>({
    //   nzTitle: title,
    //   nzContent: OperationDetailComponent,
    //   nzWrapClassName: 'user-drawer',
    //   nzBodyStyle: {
    //     padding: '0 15px 15px 15px'
    //   },
    //   nzContentParams: {
    //     operationId: event.id
    //   }
    // });
    //
    // drawerRef.afterClose.subscribe(data => {
    //   console.log(data);
    //   if (typeof data === 'string') {
    //     console.log(data);
    //   }
    // });
  }

  search() {
    console.log(this.searchForm.value);
    if (this.searchForm.value) {
      this.searchValue.set(this.searchForm.value.search || 'undefined');
      this.branchId.set(this.searchForm.value.branch || 'undefined');
      this.reloadBookings();
    }
  }

  export() {
    const params = {
      ...this.searchForm.value,
    };
    return this.service.export('appointments.xlsx', params);
  }

  branchSelected($event: any) {
    this.branchId.set($event);
    this.reloadBookings();
  }

  clearSearch() {
    this.searchForm.reset();
    this.search();
    this.searchValue.set(undefined);
    this.branchId.set(undefined);
  }

  getDetail(row: any) {
    if (row.operationDetails.length) {
      switch (row.operationType) {
        case 'EVENT':
          return row.operationDetails[0].service.name;
        default:
          return 'N/A';
      }
    }

    return 'N/A';
  }

  changeCalendar($event: (Date | null)[]) {
    this.searchForm.patchValue({
      startDate: $event[0] && format($event[0], 'yyyy-MM-dd 00:00:00'),
      endDate: $event[1] && format($event[1], 'yyyy-MM-dd 23:59:59'),
    });
    this.reloadBookings();
  }

  handleEventClick(booking: any) {
    // const modal = this.modalService.create({
    //   nzClosable: false,
    //   nzMaskClosable: false,
    //   nzTitle: this.translateService.instant('calendar.planVisit'),
    //   nzContent: AddAppointmentComponent,
    //   nzWidth: '1100px',
    //   nzStyle: { top: '0px' },
    //   nzBodyStyle: { padding: '0' },
    //   nzData: {
    //     bookingId: bookingId,
    //   },
    // });
    // modal.afterClose.subscribe((res) => {
    //   this.getUsers();
    // });

    if (!booking.visitId) {
      const modal = this.modalService.create({
        nzClosable: false,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddAppointmentComponent,
        nzWidth: '1100px',
        nzStyle: { top: '0px' },
        nzBodyStyle: { padding: '0' },
        nzFooter: null,
        nzData: {
          bookingId: booking.id
        }
      });
      modal.afterClose.pipe(untilDestroyed(this)).subscribe(() => {
        this.reloadBookings();
      });
    } else {

      const modal = this.drawerService.create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: VisitManageComponent,
        nzWidth: '1024px',
        nzBodyStyle: { padding: '0' },
        nzData: {
          visitId: booking.visitId
        }
      });

      modal.afterClose.pipe(untilDestroyed(this)).subscribe(() => {
        this.reloadBookings();
      });

    }
  }
}
